import React, { useEffect, useMemo, useState } from "react"
import { Radio, Checkbox, Input, InputNumber, Button, Table, Spin, message } from "antd"
import type { TableColumnsType } from "antd"
import { CheckOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import clsx from "clsx"
import type {
    AttachmentItem,
    EmploymentInfo,
    ReviewWorktime,
    SignedReasonType,
    SignedReviewDetail,
    SignedReviewFormValue,
} from "../types"
import {
    B_CATEGORY_OPTIONS,
    NON_BRAND_REASONS,
    SIGNED_REASON_OPTIONS,
    normalizeSignedReasonTypes,
} from "../constants"
import { validateSignedForm } from "../validate"
import {
    getProjectGrossProfitReviewWorktimesApi,
    saveProjectGrossProfitReviewDraftApi,
    submitProjectGrossProfitReviewApi,
} from "@/services/apis/project"
import EmploymentInfoSearchSelect from "./EmploymentInfoSearchSelect"
import {
    ExternalLink,
    FileUploader,
    FieldLabel,
    FormFooter,
    MilestoneTable,
    Money,
    PageHeader,
    Percent,
    RestoreBanner,
    SectionCard,
    formatHours,
    formatPercent,
} from "./shared"

const initValue = (): SignedReviewFormValue => ({
    reasonTypes: [],
    a1Checked: false,
    a1Worktimes: [],
    a1Note: "",
    a2Checked: false,
    a3Checked: false,
    a3Desc: "",
    bCategory: "",
    b0Desc: "",
    bReasons: {},
    bReasonDesc: {},
    improvement: "",
    attachments: [],
})

/**
 * 表单初始值：存在当前复盘自己的保存内容且 kind 匹配时，以保存值覆盖空值（回填）；
 * 无保存内容、kind 不匹配或 payload 为空时退回空值（spec 4.8）。
 */
const buildInitialValue = (detail: SignedReviewDetail): SignedReviewFormValue => {
    const empty = initValue()
    const saved = detail.savedSubmission
    if (!saved || saved.kind !== "signed" || !saved.payload) return empty
    const payload = saved.payload as Partial<SignedReviewFormValue>
    // 集合 / 数组字段类型稳定，避免保存内容缺字段时渲染异常
    return {
        ...empty,
        ...payload,
        // 亏损原因判定多选：兼容历史单值保存内容（reasonType）
        reasonTypes: normalizeSignedReasonTypes(payload),
        a1Worktimes: Array.isArray(payload.a1Worktimes) ? payload.a1Worktimes : [],
        bReasons: { ...(payload.bReasons ?? {}) },
        bReasonDesc: { ...(payload.bReasonDesc ?? {}) },
        attachments: Array.isArray(payload.attachments) ? payload.attachments : [],
    }
}

/** 保存内容中手动添加的员工（source manual / id 为 0）需还原到 addedEmployees，避免回填后丢失 */
const extractAddedEmployees = (value: SignedReviewFormValue): ReviewWorktime[] =>
    value.a1Worktimes.filter((w) => w.source === "manual" || w.id === 0)

/** 判断详情是否命中当前表单类型的保存内容（决定是否展示回填提示条） */
const hasSavedSubmission = (detail: SignedReviewDetail): boolean =>
    !!detail.savedSubmission && detail.savedSubmission.kind === "signed" && !!detail.savedSubmission.payload

const SignedReviewForm: React.FC<{
    detail: SignedReviewDetail
    onBack: () => void
    /** 提交成功回调：容器据此把列表项状态就地置为 submitted（spec 5.3） */
    onSubmitted?: (id: string) => void
}> = ({ detail, onBack, onSubmitted }) => {
    // 初始值：命中保存内容时回填（spec 4.8）
    const initialValue = useMemo(() => buildInitialValue(detail), [detail])
    const [value, setValue] = useState<SignedReviewFormValue>(initialValue)
    const [submitting, setSubmitting] = useState(false)
    const [savingDraft, setSavingDraft] = useState(false)
    // A1 成员工时列表：懒加载，成功后缓存；失败展示错误态与「重试」
    const [worktimes, setWorktimes] = useState<ReviewWorktime[]>([])
    const [worktimesLoading, setWorktimesLoading] = useState(false)
    const [worktimesLoaded, setWorktimesLoaded] = useState(false)
    const [worktimesError, setWorktimesError] = useState(false)
    // A1 手动添加的员工（通过「添加员工」从用工信息搜索结果补充，无成员工时记录 ID）
    // 回填时按保存内容还原，避免再次打开表单后手动添加的员工丢失
    const [addedEmployees, setAddedEmployees] = useState<ReviewWorktime[]>(() =>
        extractAddedEmployees(initialValue)
    )
    const [showAddEmployee, setShowAddEmployee] = useState(false)
    const { metrics } = detail
    const restored = hasSavedSubmission(detail)

    const patch = (p: Partial<SignedReviewFormValue>) => setValue((v) => ({ ...v, ...p }))

    // 保存内容读取失败：按空表单处理并提示，仍可正常填报（spec 4.8）
    useEffect(() => {
        if (detail.savedSubmissionError) message.warning("暂存内容加载失败")
    }, [detail.savedSubmissionError])

    // 回填后 a1Checked 为真时按既有懒加载逻辑请求成员工时列表
    useEffect(() => {
        if (value.a1Checked && !worktimesLoaded && !worktimesError && !worktimesLoading) {
            void loadWorktimes()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.a1Checked])

    // A1 工时列表 = 项目成员工时记录 + 手动添加的员工（按工号去重）；
    // 保存内容中已勾选、但接口与手动列表均不含的记录仍保留展示（不静默丢失）
    const mergedWorktimes = useMemo(() => {
        const base = [
            ...worktimes,
            ...addedEmployees.filter((e) => !worktimes.some((w) => w.clerkCode === e.clerkCode)),
        ]
        const orphans = value.a1Worktimes.filter(
            (w) => !base.some((b) => b.clerkCode === w.clerkCode)
        )
        return [...base, ...orphans]
    }, [worktimes, addedEmployees, value.a1Worktimes])

    const loadWorktimes = async (force = false) => {
        if (worktimesLoading) return
        if (!force && worktimesLoaded) return
        setWorktimesLoading(true)
        setWorktimesError(false)
        try {
            const list = await getProjectGrossProfitReviewWorktimesApi(detail.id)
            setWorktimes(list)
            setWorktimesLoaded(true)
        } catch {
            setWorktimesError(true)
        } finally {
            setWorktimesLoading(false)
        }
    }

    const toggleA1 = (checked: boolean) => {
        patch(checked ? { a1Checked: true } : { a1Checked: false, a1Worktimes: [] })
        if (!checked) {
            setAddedEmployees([])
            setShowAddEmployee(false)
        }
        if (checked && !worktimesLoaded && !worktimesError && !worktimesLoading) {
            void loadWorktimes()
        }
    }

    // 「添加员工」：从用工信息搜索结果追加到 A1 工时列表并自动勾选；同一员工不允许重复添加
    const handleAddEmployee = (info: EmploymentInfo) => {
        if (mergedWorktimes.some((w) => w.clerkCode === info.clerkCode)) {
            message.warning("该员工已在列表中")
            return
        }
        const added: ReviewWorktime = {
            source: "manual",
            id: 0,
            clerkCode: info.clerkCode,
            name: info.name,
            majorName: info.majorName,
            companyName: info.companyName,
            departmentName: "",
            totalHours: 0,
            workday: 0,
        }
        setAddedEmployees((list) => [...list, added])
        setValue((v) => ({ ...v, a1Worktimes: [...v.a1Worktimes, added] }))
    }

    // 勾选/取消勾选填错人员（保留已填写的修正后的工日数）
    const handleWorktimeSelectionChange = (keys: React.Key[]) => {
        setValue((v) => ({
            ...v,
            a1Worktimes: keys
                .map((k) => {
                    const clerkCode = String(k)
                    const prev = v.a1Worktimes.find((w) => w.clerkCode === clerkCode)
                    if (prev) return prev
                    const row = mergedWorktimes.find((w) => w.clerkCode === clerkCode)
                    return row ? { ...row } : null
                })
                .filter((w): w is ReviewWorktime => w !== null),
        }))
    }

    // 编辑修正后的工日数：仅允许大于等于 0 的数字，非法值不保存
    const handleCorrectedHoursChange = (clerkCode: string, v: number | null) => {
        if (v === null) {
            setValue((prev) => ({
                ...prev,
                a1Worktimes: prev.a1Worktimes.map((w) =>
                    w.clerkCode === clerkCode ? { ...w, correctedTotalHours: undefined } : w
                ),
            }))
            return
        }
        if (isNaN(v) || v < 0) {
            message.warning("修正后的工日数仅支持大于等于 0 的数字")
            return
        }
        setValue((prev) => ({
            ...prev,
            a1Worktimes: prev.a1Worktimes.map((w) =>
                w.clerkCode === clerkCode ? { ...w, correctedTotalHours: v } : w
            ),
        }))
    }

    const worktimeColumns: TableColumnsType<ReviewWorktime> = [
        { title: "工号", dataIndex: "clerkCode", key: "clerkCode", width: 100, render: (v: string) => v || "-" },
        { title: "姓名", dataIndex: "name", key: "name", width: 100, render: (v: string) => v || "-" },
        { title: "专业", dataIndex: "majorName", key: "majorName", width: 120, render: (v: string) => v || "-" },
        {
            title: "工日数",
            dataIndex: "workday",
            key: "workday",
            width: 110,
            align: "right",
            render: (v: number) => formatHours(v),
        },
        {
            title: "类型",
            dataIndex: "source",
            key: "source",
            width: 100,
            // 只读展示来源：系统汇总 / 手动添加，不允许手工修改
            render: (v: ReviewWorktime["source"]) => (v === "manual" ? "手动添加" : "系统"),
        },
        {
            title: "修正后的工日数",
            key: "correctedTotalHours",
            width: 150,
            align: "right",
            render: (_, row) => {
                const selected = value.a1Worktimes.find((w) => w.clerkCode === row.clerkCode)
                return (
                    <InputNumber
                        size="small"
                        precision={1}
                        placeholder="选填"
                        disabled={!selected}
                        value={selected?.correctedTotalHours ?? null}
                        onChange={(v) => handleCorrectedHoursChange(row.clerkCode, v)}
                    />
                )
            },
        },
    ]

    // 大项 A / B 多选：点击切换选中态；取消某一大项时收起其填写区域并清空已填内容（不参与提交）
    const toggleReasonType = (r: SignedReasonType) => {
        const selected = value.reasonTypes ?? []
        if (selected.includes(r)) {
            if (r === "A") {
                patch({
                    reasonTypes: selected.filter((x) => x !== r),
                    a1Checked: false,
                    a1Worktimes: [],
                    a1Note: "",
                    a2Checked: false,
                    a3Checked: false,
                    a3Desc: "",
                })
                setAddedEmployees([])
                setShowAddEmployee(false)
            } else {
                patch({
                    reasonTypes: selected.filter((x) => x !== r),
                    bCategory: "",
                    b0Desc: "",
                    bReasons: {},
                    bReasonDesc: {},
                })
            }
            return
        }
        patch({ reasonTypes: [...selected, r] })
        if (r === "A" && !worktimesLoaded && !worktimesError && !worktimesLoading) {
            void loadWorktimes()
        }
    }

    const validate = (): string | null =>
        validateSignedForm(value, {
            loaded: worktimesLoaded,
            error: worktimesError,
            count: worktimes.length,
        })

    const submit = async () => {
        const err = validate()
        if (err) {
            message.warning(err)
            return
        }
        setSubmitting(true)
        try {
            await submitProjectGrossProfitReviewApi(detail.id, { kind: "signed", signed: value })
            message.success("提交成功")
            onSubmitted?.(detail.id)
            onBack()
        } catch (e) {
            message.error(typeof e === "string" ? e : "提交失败")
        } finally {
            setSubmitting(false)
        }
    }

    const saveDraft = async () => {
        setSavingDraft(true)
        try {
            await saveProjectGrossProfitReviewDraftApi(detail.id, { kind: "signed", signed: value })
            message.success("已暂存")
        } catch (e) {
            message.error(typeof e === "string" ? e : "暂存失败")
        } finally {
            setSavingDraft(false)
        }
    }

    return (
        <div className="min-h-full flex flex-col">
            <PageHeader
                title="已签约项目毛利复盘"
                subtitle={`${detail.projectName}（${detail.projectNo}）`}
                onBack={onBack}
            />

            <div className="flex-1 p-5 space-y-4">
                {restored && detail.savedSubmission?.status === "draft" && (
                    <RestoreBanner
                        status={detail.savedSubmission.status}
                        submittedAt={detail.savedSubmission.submittedAt}
                    />
                )}

                {/* 项目信息（表格化布局：深色标题栏 + 下划线信息项 + 指标表格） */}
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    {/* 深色标题栏 */}
                    <header className="bg-[#1c4d38] px-5 py-3">
                        <h3 className="text-[15px] leading-6 font-semibold text-white">项目信息</h3>
                    </header>
                    <div className="p-5">
                        {/* 基础信息：标签 + 下划线取值 */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-5 mb-6">
                            {[
                                { label: "项目名称", value: detail.projectName },
                                { label: "项目编号", value: detail.projectNo },
                                { label: "所属所 / 管理所长", value: `${detail.department} / ${detail.manager}` },
                                { label: "复盘发送月份", value: detail.reviewMonth },
                                { label: "指标", value: metrics.target },
                            ].map((it) => (
                                <div key={it.label} className="min-w-0">
                                    <div className="text-xs text-gray-400 mb-1.5">{it.label}</div>
                                    <div className="text-sm text-gray-800 font-medium break-words border-b border-gray-300 pb-1.5">
                                        {it.value}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* 毛利指标表格：深色表头 + 白底行 */}
                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                            <table className="w-full min-w-[840px] border-separate border-spacing-0 text-sm">
                                <thead>
                                    <tr>
                                        <th className="w-32 bg-dark-bg px-4 py-2.5 text-left font-medium text-white">
                                            指标
                                        </th>
                                        {["完成产值", "完成产值毛利", "完成产值毛利率", "实收产值", "实收毛利率"].map((h) => (
                                            <th
                                                key={h}
                                                className="bg-dark-bg px-4 py-2.5 text-left font-medium text-white"
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {metrics.periods.map((p) => (
                                        <tr key={p.label} className="hover:bg-gray-50">
                                            <td className="px-4 py-2.5 text-gray-700 border-t border-gray-100">
                                                {p.label}
                                            </td>
                                            <td className="px-4 py-2.5 font-medium border-t border-gray-100">
                                                <Money value={p.completionOutput} />
                                            </td>
                                            <td className="px-4 py-2.5 font-medium border-t border-gray-100">
                                                <Money value={p.completionProfit} />
                                            </td>
                                            <td className="px-4 py-2.5 font-medium border-t border-gray-100">
                                                <Percent value={p.completionProfitRate} />
                                            </td>
                                            <td className="px-4 py-2.5 font-medium border-t border-gray-100">
                                                <Money value={p.actualIncome} />
                                            </td>
                                            <td className="px-4 py-2.5 font-medium border-t border-gray-100">
                                                <Percent value={p.actualProfitRate} />
                                            </td>
                                        </tr>
                                    ))}
                                    {metrics.periods.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-10 text-center text-gray-400 border-t border-gray-100">
                                                暂无毛利指标数据
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* 投入曲线图链接 */}
                        <div className="mt-4">
                            <ExternalLink href={detail.curveUrl} className="!text-red-500 hover:!text-red-600 text-base font-medium">
                                投入曲线图链接
                            </ExternalLink>
                        </div>

                        {detail.lastReview && (
                            <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
                                <div className="text-xs text-gray-400 mb-1">上次复盘内容（需对比改进举措是否落地）</div>
                                {detail.lastReview}
                            </div>
                        )}
                    </div>
                </section>

                {/* 亏损原因判定（A / B 可多选） */}
                <SectionCard
                    title="亏损原因判定"
                    required
                    desc="可多选（A / B 可同时勾选，至少选择一项）；选中哪项就必须描述哪项，不填视为未选此项"
                >
                    <div className="grid grid-cols-1 gap-3">
                        {SIGNED_REASON_OPTIONS.map((opt) => {
                            const active = (value.reasonTypes ?? []).includes(opt.value)
                            return (
                                <div key={opt.value}>
                                    <div
                                        onClick={() => toggleReasonType(opt.value)}
                                        className={clsx(
                                            "flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors",
                                            active ? "border-dark-bg bg-dark-bg/5" : "border-gray-200 hover:border-gray-300"
                                        )}
                                    >
                                        <span
                                            className={clsx(
                                                "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                                                active
                                                    ? "border-dark-bg bg-dark-bg text-white"
                                                    : "border-gray-300 bg-white"
                                            )}
                                        >
                                            {active && <CheckOutlined style={{ fontSize: 10 }} />}
                                        </span>
                                        <div>
                                            <div className="text-sm font-medium text-gray-800">{opt.title}</div>
                                            <div className="text-xs text-gray-400 mt-0.5">{opt.desc}</div>
                                        </div>
                                    </div>

                                    {opt.value === "A" && active && (
                                        <div className="mt-5 space-y-4">
                                            {/* A1 */}
                                            <div className="rounded-lg border border-gray-200 p-4">
                                                <Checkbox checked={value.a1Checked} onChange={(e) => toggleA1(e.target.checked)}>
                                                    <span className="text-sm font-medium text-gray-800">A1 工时填报错误</span>
                                                </Checkbox>
                                                {value.a1Checked && (
                                                    <div className="mt-3 pl-6 space-y-3">
                                                        {/* 添加员工：从用工信息搜索结果补充填错人员 */}
                                                        <div className="space-y-2">
                                                            <Button size="small" onClick={() => setShowAddEmployee((s) => !s)}>
                                                                添加员工
                                                            </Button>
                                                            {showAddEmployee && (
                                                                <EmploymentInfoSearchSelect
                                                                    className="w-full"
                                                                    onSelect={handleAddEmployee}
                                                                />
                                                            )}
                                                        </div>
                                                        {worktimesLoading ? (
                                                            <div className="py-6 text-center">
                                                                <Spin size="small" />
                                                            </div>
                                                        ) : worktimesError ? (
                                                            <div className="flex items-center gap-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-500">
                                                                成员工时列表加载失败
                                                                <Button size="small" onClick={() => void loadWorktimes(true)}>
                                                                    重试
                                                                </Button>
                                                            </div>
                                                        ) : mergedWorktimes.length === 0 ? (
                                                            <div className="py-6 text-center text-sm text-gray-400">
                                                                该项目暂无成员工时记录
                                                            </div>
                                                        ) : (
                                                            <Table<ReviewWorktime>
                                                                size="small"
                                                                rowKey="clerkCode"
                                                                columns={worktimeColumns}
                                                                dataSource={mergedWorktimes}
                                                                pagination={false}
                                                                scroll={{ x: 920 }}
                                                                rowSelection={{
                                                                    columnTitle: "勾选",
                                                                    selectedRowKeys: value.a1Worktimes.map((w) => w.clerkCode),
                                                                    onChange: handleWorktimeSelectionChange,
                                                                }}
                                                            />
                                                        )}
                                                        <div>
                                                            <FieldLabel>补充说明</FieldLabel>
                                                            <Input.TextArea
                                                                rows={2}
                                                                placeholder="可补充说明填错的工时情况（选填）"
                                                                value={value.a1Note}
                                                                onChange={(e) => patch({ a1Note: e.target.value })}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* A2 */}
                                            <div className="rounded-lg border border-gray-200 p-4">
                                                <Checkbox
                                                    checked={value.a2Checked}
                                                    onChange={(e) => patch({ a2Checked: e.target.checked })}
                                                >
                                                    <span className="text-sm font-medium text-gray-800">A2 里程碑填报错误</span>
                                                </Checkbox>
                                                {value.a2Checked && (
                                                    <div className="mt-3 pl-6">
                                                        {/* 无填写项：展示里程碑只读列表，并保留按 opProjectID 拼接的跳转链接，另起页面打开 */}
                                                        <MilestoneTable list={detail.milestones ?? []} />
                                                        <div className="mt-3">
                                                            <ExternalLink href={detail.milestoneUrl}>里程碑链接</ExternalLink>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* A3 */}
                                            <div className="rounded-lg border border-gray-200 p-4">
                                                <Checkbox
                                                    checked={value.a3Checked}
                                                    onChange={(e) => patch({ a3Checked: e.target.checked })}
                                                >
                                                    <span className="text-sm font-medium text-gray-800">
                                                        A3 立项、口径或数据源有误
                                                    </span>
                                                </Checkbox>
                                                {value.a3Checked && (
                                                    <div className="mt-3 pl-6">
                                                        <Input.TextArea
                                                            rows={2}
                                                            placeholder="请填写具体描述（事实 + 数据辅助）"
                                                            value={value.a3Desc}
                                                            onChange={(e) => patch({ a3Desc: e.target.value })}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {opt.value === "B" && active && (
                                        <div className="mt-5 space-y-4">
                                            {/* 品牌项目 / 非品牌项目 二选一 */}
                                            <Radio.Group
                                                value={value.bCategory}
                                                onChange={(e) => patch({ bCategory: e.target.value })}
                                                className="w-full"
                                            >
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    {B_CATEGORY_OPTIONS.map((bOpt) => (
                                                        <div
                                                            key={bOpt.value}
                                                            onClick={() => patch({ bCategory: bOpt.value })}
                                                            className={clsx(
                                                                "flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors",
                                                                value.bCategory === bOpt.value
                                                                    ? "border-dark-bg bg-dark-bg/5"
                                                                    : "border-gray-200 hover:border-gray-300"
                                                            )}
                                                        >
                                                            <Radio value={bOpt.value} />
                                                            <div>
                                                                <div className="text-sm font-medium text-gray-800">{bOpt.label}</div>
                                                                <div className="text-xs text-gray-400 mt-0.5">{bOpt.desc}</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Radio.Group>

                                            {/* 品牌项目 */}
                                            {value.bCategory === "brand" && (
                                                <div className="rounded-lg border border-gray-200 p-4">
                                                    <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-600">
                                                        预计毛利率：
                                                        <span
                                                            className={
                                                                (detail.expectedProfitRate ?? 0) < 0
                                                                    ? "font-semibold text-red-500"
                                                                    : "font-semibold text-gray-800"
                                                            }
                                                        >
                                                            {formatPercent(detail.expectedProfitRate)}
                                                        </span>
                                                        <span className="text-xs text-gray-400 ml-3">
                                                            当年累计完成产值毛利率：
                                                            {formatPercent(
                                                                metrics.periods.find((p) => p.label === "当年累计")
                                                                    ?.completionProfitRate
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="mt-3">
                                                        <Input.TextArea
                                                            rows={3}
                                                            placeholder="请描述：与预计毛利率对比的差异原因、止损边界与退出条件"
                                                            value={value.b0Desc}
                                                            onChange={(e) => patch({ b0Desc: e.target.value })}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {/* 非品牌项目 */}
                                            {value.bCategory === "non_brand" && (
                                                <div className="rounded-lg border border-gray-200 p-4">
                                                    <div className="text-sm font-medium text-gray-800">
                                                        非品牌项目
                                                        <span className="text-xs text-gray-400 ml-2">
                                                            勾选原因类别，每选一项必须填写该项专属描述
                                                        </span>
                                                    </div>
                                                    <div className="mt-3 space-y-3">
                                                        {NON_BRAND_REASONS.map((r) => (
                                                            <div key={r.key}>
                                                                <Checkbox
                                                                    checked={!!value.bReasons[r.key]}
                                                                    onChange={(e) =>
                                                                        patch({
                                                                            bReasons: {
                                                                                ...value.bReasons,
                                                                                [r.key]: e.target.checked,
                                                                            },
                                                                        })
                                                                    }
                                                                >
                                                                    <span className="text-sm text-gray-700">
                                                                        {r.key} {r.label}
                                                                    </span>
                                                                </Checkbox>
                                                                {value.bReasons[r.key] && (
                                                                    <div className="mt-2 pl-6">
                                                                        <Input.TextArea
                                                                            rows={2}
                                                                            placeholder="出了什么问题 - 事实 + 数据辅助（必填）"
                                                                            value={value.bReasonDesc[r.key] || ""}
                                                                            onChange={(e) =>
                                                                                patch({
                                                                                    bReasonDesc: {
                                                                                        ...value.bReasonDesc,
                                                                                        [r.key]: e.target.value,
                                                                                    },
                                                                                })
                                                                            }
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </SectionCard>

                {/* 改进举措 */}
                <SectionCard
                    title="改进举措"
                    required
                    desc="做什么 + 谁负责 + 何时完成 + 衡量标准，禁止空话套话"
                >
                    <Input.TextArea
                        rows={4}
                        placeholder="示例：7月20日前张三与甲方确认重做范围并签补充协议 50 万元"
                        value={value.improvement}
                        onChange={(e) => patch({ improvement: e.target.value })}
                    />
                </SectionCard>

                {/* 附件 */}
                <SectionCard
                    title="附件"
                    desc="补充协议、会议纪要 / 公司认可文件、数据修正说明、其他（支持 pdf / xlsx / jpg）"
                >
                    <FileUploader
                        value={value.attachments}
                        onChange={(attachments: AttachmentItem[]) => patch({ attachments })}
                    />
                </SectionCard>
            </div>

            <FormFooter
                submitter={detail.manager}
                date={dayjs().format("YYYY-MM-DD")}
                submitting={submitting}
                savingDraft={savingDraft}
                onSaveDraft={saveDraft}
                onSubmit={submit}
            />
        </div>
    )
}

export default SignedReviewForm
